import { 
  collection, 
  doc, 
  setDoc, 
  getDocs, 
  updateDoc, 
  query, 
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  addDoc,
  limit,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Recommendation } from '../types';
import { RecommendationEngine } from './recommendationEngine';
import { TradingRulesService } from './tradingRulesService';

export class RecommendationService {
  private static RECOMMENDATIONS_COLLECTION = 'recommendations';
  private static LAST_RUN_DOC = 'metadata/recommendation_last_run';

  /**
   * Initialize the recommendation service
   * Sets up default trading rules if needed
   */
  static async initialize(): Promise<void> {
    try {
      await TradingRulesService.initializeDefaultRules();
      console.log('Recommendation service initialized');
    } catch (error) {
      console.error('Error initializing recommendation service:', error);
      throw error;
    }
  }

  /**
   * Main entry point - generates and stores recommendations
   * Called after data updates (MarketSurge import or price refresh)
   */
  static async generateAndStoreRecommendations(
    updateSource: 'marketsurge' | 'twelvedata' | 'yfinance' | 'finnhub' | 'alpaca' | 'manual' = 'manual'
  ): Promise<Recommendation[]> {
    try {
      console.log(`Generating recommendations triggered by ${updateSource} update`);
      
      // Check if we should run (debounce to prevent rapid re-runs)
      const shouldRun = await this.shouldGenerateRecommendations();
      if (!shouldRun) {
        console.log('Skipping recommendation generation - ran too recently');
        return [];
      }

      // Mark as running
      await this.markRecommendationRun();

      // Generate recommendations using the engine
      const recommendations = await RecommendationEngine.generateRecommendations();
      
      // Store in Firestore
      await this.storeRecommendations(recommendations);
      
      // Mark any expired recommendations as inactive
      await this.expireOldRecommendations();
      
      console.log(`Generated ${recommendations.length} recommendations`);
      
      // Execute autopilot for enabled portfolios
      try {
        const { AutopilotService } = await import('./autopilotService');
        await AutopilotService.executeAutopilotRecommendations();
      } catch (error) {
        console.error('Error executing autopilot:', error);
        // Don't fail the entire process if autopilot fails
      }
      
      return recommendations;
    } catch (error) {
      console.error('Error generating recommendations:', error);
      throw error;
    }
  }

  /**
   * Get active recommendations for a portfolio
   */
  static async getPortfolioRecommendations(
    portfolioId: string,
    type?: Recommendation['type']
  ): Promise<Recommendation[]> {
    try {
      let q = query(
        collection(db, this.RECOMMENDATIONS_COLLECTION),
        where('portfolioId', '==', portfolioId),
        where('executed', '==', false),
        orderBy('generatedAt', 'desc'),
        orderBy('score', 'desc')
      );

      if (type) {
        q = query(
          collection(db, this.RECOMMENDATIONS_COLLECTION),
          where('portfolioId', '==', portfolioId),
          where('type', '==', type),
          where('executed', '==', false),
          orderBy('generatedAt', 'desc'),
          orderBy('score', 'desc')
        );
      }

      const snapshot = await getDocs(q);
      const recommendations: Recommendation[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        const recommendation = this.convertFromFirestore(doc.id, data);
        
        // Check if recommendation is still valid
        if (!recommendation.expiresAt || recommendation.expiresAt > new Date()) {
          recommendations.push(recommendation);
        }
      });

      return recommendations;
    } catch (error) {
      console.error('Error getting portfolio recommendations:', error);
      throw error;
    }
  }

  /**
   * Get all active recommendations (for global view)
   */
  static async getAllActiveRecommendations(): Promise<Recommendation[]> {
    try {
      const q = query(
        collection(db, this.RECOMMENDATIONS_COLLECTION),
        where('executed', '==', false),
        orderBy('generatedAt', 'desc'),
        orderBy('score', 'desc'),
        limit(50) // Limit to top 50 recommendations
      );

      const snapshot = await getDocs(q);
      const recommendations: Recommendation[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        const recommendation = this.convertFromFirestore(doc.id, data);
        
        // Check if recommendation is still valid
        if (!recommendation.expiresAt || recommendation.expiresAt > new Date()) {
          recommendations.push(recommendation);
        }
      });

      return recommendations;
    } catch (error) {
      console.error('Error getting all recommendations:', error);
      throw error;
    }
  }

  /**
   * Acknowledge a recommendation (mark as viewed)
   */
  static async acknowledgeRecommendation(recommendationId: string): Promise<void> {
    try {
      const docRef = doc(db, this.RECOMMENDATIONS_COLLECTION, recommendationId);
      await updateDoc(docRef, {
        acknowledged: true,
        acknowledgedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error acknowledging recommendation:', error);
      throw error;
    }
  }

  /**
   * Mark recommendation as executed
   */
  static async markRecommendationExecuted(
    recommendationId: string,
    transactionId?: string
  ): Promise<void> {
    try {
      const docRef = doc(db, this.RECOMMENDATIONS_COLLECTION, recommendationId);
      await updateDoc(docRef, {
        executed: true,
        executedAt: serverTimestamp(),
        executedTransactionId: transactionId || null
      });
    } catch (error) {
      console.error('Error marking recommendation as executed:', error);
      throw error;
    }
  }

  /**
   * Dismiss a recommendation (user chose not to act)
   */
  static async dismissRecommendation(recommendationId: string): Promise<void> {
    try {
      const docRef = doc(db, this.RECOMMENDATIONS_COLLECTION, recommendationId);
      await updateDoc(docRef, {
        dismissed: true,
        dismissedAt: serverTimestamp(),
        executed: true // Treat as executed to remove from active list
      });
    } catch (error) {
      console.error('Error dismissing recommendation:', error);
      throw error;
    }
  }

  /**
   * Get recommendation history for analysis
   */
  static async getRecommendationHistory(
    portfolioId?: string,
    daysBack: number = 30
  ): Promise<Recommendation[]> {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysBack);

      let q = query(
        collection(db, this.RECOMMENDATIONS_COLLECTION),
        where('generatedAt', '>=', Timestamp.fromDate(cutoffDate)),
        orderBy('generatedAt', 'desc')
      );

      if (portfolioId) {
        q = query(
          collection(db, this.RECOMMENDATIONS_COLLECTION),
          where('portfolioId', '==', portfolioId),
          where('generatedAt', '>=', Timestamp.fromDate(cutoffDate)),
          orderBy('generatedAt', 'desc')
        );
      }

      const snapshot = await getDocs(q);
      const recommendations: Recommendation[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        recommendations.push(this.convertFromFirestore(doc.id, data));
      });

      return recommendations;
    } catch (error) {
      console.error('Error getting recommendation history:', error);
      throw error;
    }
  }

  /**
   * Get executed recommendations for history view
   * Returns executed (non-dismissed) recommendations sorted by execution date
   */
  static async getExecutedRecommendations(
    portfolioId?: string,
    daysBack: number = 30,
    maxResults: number = 100
  ): Promise<Recommendation[]> {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysBack);

      // Query for executed recommendations
      // Note: Firestore requires executedAt to exist for inequality filter
      let q = query(
        collection(db, this.RECOMMENDATIONS_COLLECTION),
        where('executed', '==', true),
        where('executedAt', '>=', Timestamp.fromDate(cutoffDate)),
        orderBy('executedAt', 'desc'),
        limit(maxResults)
      );

      if (portfolioId) {
        q = query(
          collection(db, this.RECOMMENDATIONS_COLLECTION),
          where('portfolioId', '==', portfolioId),
          where('executed', '==', true),
          where('executedAt', '>=', Timestamp.fromDate(cutoffDate)),
          orderBy('executedAt', 'desc'),
          limit(maxResults)
        );
      }

      const snapshot = await getDocs(q);
      const recommendations: Recommendation[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        const recommendation = this.convertFromFirestore(doc.id, data);

        // Filter out dismissed recommendations client-side
        if (!recommendation.dismissed) {
          recommendations.push(recommendation);
        }
      });

      return recommendations;
    } catch (error) {
      console.error('Error getting executed recommendations:', error);
      throw error;
    }
  }

  /**
   * Store recommendations in Firestore
   */
  private static async storeRecommendations(
    recommendations: Recommendation[]
  ): Promise<void> {
    const batch = writeBatch(db);
    
    for (const recommendation of recommendations) {
      // Create a new document for each recommendation
      const docRef = doc(collection(db, this.RECOMMENDATIONS_COLLECTION));
      
      // Convert to Firestore format
      const firestoreData = this.convertToFirestore(recommendation);
      
      batch.set(docRef, firestoreData);
    }
    
    await batch.commit();
    console.log(`Stored ${recommendations.length} recommendations`);
  }

  /**
   * Expire old recommendations
   */
  private static async expireOldRecommendations(): Promise<void> {
    try {
      const now = new Date();
      
      const q = query(
        collection(db, this.RECOMMENDATIONS_COLLECTION),
        where('executed', '==', false),
        where('expiresAt', '<', Timestamp.fromDate(now))
      );

      const snapshot = await getDocs(q);
      const batch = writeBatch(db);

      snapshot.forEach((doc) => {
        batch.update(doc.ref, {
          executed: true,
          expiredAt: serverTimestamp()
        });
      });

      await batch.commit();
      console.log(`Expired ${snapshot.size} old recommendations`);
    } catch (error) {
      console.error('Error expiring recommendations:', error);
    }
  }

  /**
   * Check if we should generate recommendations (debounce)
   */
  private static async shouldGenerateRecommendations(): Promise<boolean> {
    try {
      const docRef = doc(db, this.LAST_RUN_DOC);
      const docSnap = await getDocs(query(collection(db, 'metadata'), where('type', '==', 'recommendation_last_run'), limit(1)));
      
      if (!docSnap.empty) {
        const lastRun = docSnap.docs[0].data().lastRun?.toDate();
        if (lastRun) {
          const minutesSinceLastRun = (Date.now() - lastRun.getTime()) / (1000 * 60);
          return minutesSinceLastRun >= 1; // Run at most every 1 minute (temporarily reduced from 5)
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error checking last run:', error);
      return true; // Generate on error
    }
  }

  /**
   * Mark that recommendations were generated
   */
  private static async markRecommendationRun(): Promise<void> {
    try {
      const metadataCollection = collection(db, 'metadata');
      const q = query(metadataCollection, where('type', '==', 'recommendation_last_run'), limit(1));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        // Create new document
        await addDoc(metadataCollection, {
          type: 'recommendation_last_run',
          lastRun: serverTimestamp()
        });
      } else {
        // Update existing
        await updateDoc(snapshot.docs[0].ref, {
          lastRun: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error marking recommendation run:', error);
    }
  }

  /**
   * Convert Firestore data to Recommendation type
   */
  private static convertFromFirestore(id: string, data: any): Recommendation {
    return {
      ...data,
      id,
      generatedAt: data.generatedAt?.toDate() || new Date(),
      expiresAt: data.expiresAt?.toDate(),
      acknowledgedAt: data.acknowledgedAt?.toDate(),
      executedAt: data.executedAt?.toDate(),
    } as Recommendation;
  }

  /**
   * Convert Recommendation to Firestore format
   */
  private static convertToFirestore(recommendation: Recommendation): any {
    const data: any = {
      ...recommendation,
      generatedAt: Timestamp.fromDate(recommendation.generatedAt),
      executed: false,
      acknowledged: false
    };

    // Remove the id field (it's stored as document ID)
    delete data.id;

    // Convert optional dates
    if (recommendation.expiresAt) {
      data.expiresAt = Timestamp.fromDate(recommendation.expiresAt);
    }

    // Include trading rules version fields if present
    if (recommendation.tradingRulesId) {
      data.tradingRulesId = recommendation.tradingRulesId;
    }
    if (recommendation.tradingRulesVersion) {
      data.tradingRulesVersion = recommendation.tradingRulesVersion;
    }

    return data;
  }
}